/**
 * The ten CLM surfaces, in three nav groups, exactly as
 * docs/clm-ui-mock.html defines them.
 *
 * One table, used by three consumers — the nav rail, the router and the
 * breadcrumb — so a surface cannot appear in the navigation without a route,
 * or gain a route nobody can reach. The mock keeps the same list in three
 * places (buttons, sections, the `L` breadcrumb map); this is that list
 * normalised.
 *
 * Surface names are the mock's names verbatim. Do not "improve" them here: the
 * mock is the interface spec of record and the requirements traceability in
 * docs/requirements.md is written against these words.
 */

export type NavGroupId = 'contracts' | 'clauses' | 'governance' | 'analytics' | 'admin';

export interface SurfaceDefinition {
  /** Stable id. Matches the mock's `data-s` attribute so the two can be diffed. */
  readonly id: string;
  /** Router path, absolute. */
  readonly path: string;
  /** Label in the nav rail and the second half of the breadcrumb. */
  readonly label: string;
  /** One line of purpose, shown on the placeholder view. */
  readonly summary: string;
  /** Requirement ids this surface serves — traceability, and the crib for the story that builds it. */
  readonly requirements: readonly string[];
  /**
   * Roles that make this surface actionable. Empty everywhere in this scaffold:
   * the role taxonomy is PR-11's to name, and guessing it here would create a
   * second copy of the authorisation model. When populated it drives styling
   * ONLY — never routing. See src/auth/permissions.ts.
   */
  readonly suggested_roles?: readonly string[];
  /**
   * CLM-107 — Admin group only: a rail visibility gate of the entry's own,
   * in place of the group default (`bulkIngestAffordance`, i.e. contributor
   * or admin). Omitted means that default. `'admin_configure'` hides the entry
   * when the claims clearly name neither the admin role nor its group, and
   * dims it when they cannot tell (`adminConfigureAffordance`).
   * `'admin_or_legal'` (CLM-111) does the same for admin **or** Legal
   * (`adminOrLegalAffordance`) — the one gate that reaches a sign-in the
   * default does not, which is why the group now renders when any entry is
   * eligible rather than when the default is. Like the default it mirrors
   * what the API does (403 without the action) and decides nothing: the route
   * stays registered and the API is the boundary (NFR-06).
   */
  readonly rail_gate?: 'admin_configure' | 'admin_or_legal';
}

export interface NavGroup {
  readonly id: NavGroupId;
  /** Group heading in the rail and the first half of the breadcrumb. */
  readonly label: string;
  readonly surfaces: readonly SurfaceDefinition[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'contracts',
    label: 'Contracts',
    surfaces: [
      {
        id: 'dash',
        path: '/contracts/attention',
        label: 'Needs attention',
        summary: 'Unacknowledged alerts and the oldest unverified fields, side by side.',
        requirements: ['O-1', 'FR-04', 'FR-07', 'NFR-15'],
      },
      {
        id: 'search',
        path: '/contracts/search',
        label: 'Search',
        summary:
          'Metadata and contract content in one scope-filtered query, with a reason trace per hit.',
        requirements: ['FR-18', 'FR-20', 'FR-27', 'NFR-10'],
      },
      {
        id: 'detail',
        path: '/contracts/record',
        label: 'Contract record',
        summary:
          'The facts about one agreement: family, parties, obligations, clauses and the evidence chain behind each field.',
        requirements: ['FR-19', 'CR-01', 'NFR-07'],
      },
      {
        id: 'upload',
        path: '/contracts/new',
        label: 'Add a contract',
        summary:
          'Guided intake. The document goes to SharePoint through the API; the facts go to Postgres.',
        /**
         * CLM-30. FR-13 is the controlled manual path, FR-14 is *no free-text
         * keys* — which this surface satisfies by having no contract-key input
         * at all — and IR-05 is the document staying in SharePoint while CLM
         * stores the reference. The previous list (`FR-19`, `NFR-09`) named the
         * contract-record read and a non-functional target, neither of which is
         * what this page does.
         */
        requirements: ['FR-13', 'FR-14', 'IR-05'],
      },
    ],
  },
  {
    id: 'clauses',
    label: 'Clauses',
    surfaces: [
      {
        id: 'clauses',
        path: '/clauses',
        label: 'Clause library',
        summary:
          'Reusable standard clauses, semantically searchable in plain language. All authorised may read; only reviewers may edit master text.',
        requirements: ['FR-16', 'FR-17', 'FR-25'],
      },
      {
        id: 'review',
        path: '/clauses/match-review',
        label: 'Match review',
        summary:
          'Clause candidates mined from the corpus awaiting a Legal decision, and medium-confidence paragraph matches queued for a person — corrections that improve later results.',
        requirements: ['FR-26', 'FR-28', 'NFR-13'],
      },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    surfaces: [
      {
        id: 'verify',
        path: '/governance/verification',
        label: 'Verification queue',
        summary:
          'Human verification of extracted fields. The verification row plus the AI audit row is the ISO 42001 evidence chain.',
        requirements: ['CR-01', 'NFR-07', 'FR-28'],
      },
      {
        id: 'alias',
        path: '/governance/party-aliases',
        label: 'Party aliases',
        summary:
          'Approving the aliases that pseudonymisation substitutes, and closing residual-leak findings.',
        requirements: ['FR-29', 'NFR-13'],
      },
      {
        id: 'quality',
        path: '/governance/search-quality',
        label: 'Search quality',
        summary:
          'Judgements, boosts and ranking stability — the same query must rank the same way (O-6).',
        requirements: ['O-6', 'FR-26', 'FR-27', 'NFR-13'],
      },
      {
        id: 'notif',
        path: '/governance/notifications',
        label: 'Notifications',
        summary:
          'The alert ledger and its idempotency key, so a tier fires once and O-1 cannot fail silently.',
        requirements: ['O-1', 'FR-04', 'NFR-15'],
      },
    ],
  },
];

/**
 * The admin group — CLM-43, deliberately OUTSIDE `NAV_GROUPS`.
 *
 * The ten surfaces above are the mock verbatim; the bulk-ingest planner is not
 * in the mock, and it is the one part of the rail whose *visibility* keys off
 * claims (see NavRail for why hiding, not dimming, is right there). Keeping it
 * out of NAV_GROUPS means the three original consumers keep meaning "the ten
 * mock surfaces", while the rail, the router and the breadcrumb each opt into
 * this group explicitly.
 */
export const ADMIN_NAV_GROUP: NavGroup = {
  id: 'admin',
  label: 'Admin',
  surfaces: [
    {
      id: 'ingest',
      path: '/admin/ingest',
      label: 'Bulk ingest',
      summary:
        'Paste a SharePoint folder link and see exactly what a backfill would ingest, skip and exclude — then tick the rows you approve and execute them.',
      requirements: ['IR-05', 'IR-01', 'FR-13', 'FR-14', 'NFR-06', 'NFR-07'],
      // R-94-1 (CLM-94) widened contract:bulk_ingest to contributor; unread by
      // any renderer today (the Admin nav group gates on bulkIngestAffordance
      // as a whole, not per-surface suggested_roles), kept accurate regardless.
      suggested_roles: ['contributor', 'admin'],
    },
    /* CLM-35 — FR-15: the stored corpus against the authoritative SOW list. */
    {
      id: 'reconcile',
      path: '/admin/reconcile',
      label: 'Corpus reconciliation',
      summary:
        'Compare the stored SOWs with the authoritative list from Qfly and see every gap as a typed exception — before go-live and at cutover.',
      requirements: ['FR-15', 'NFR-09', 'NFR-06', 'NFR-07'],
      suggested_roles: ['admin'],
    },
    /* CLM-70 — pipeline status visibility (D-70-1…D-70-5). */
    {
      id: 'pipeline',
      path: '/admin/pipeline',
      label: 'Pipeline',
      summary:
        'Where every contract sits in intake → convert → pseudonymise → extract → verify, and which are held, refused or failed — with the audit row’s reason.',
      requirements: ['NFR-07', 'CR-01', 'NFR-06', 'FR-07'],
      suggested_roles: ['admin'],
    },
    /* CLM-107 — per-field extraction accuracy (FR-30, DR-08). Admin only
       (admin:configure, R-107-6), so its rail entry has its own, narrower gate. */
    {
      id: 'field-accuracy',
      path: '/admin/field-accuracy',
      label: 'Extraction accuracy',
      summary:
        'Per extracted field, how often a reviewer accepted the value as extracted and how many decisions that rests on — from the audit log, per schema version and model. A measurement, not a verdict: no threshold is set here.',
      requirements: ['FR-30', 'DR-08', 'NFR-16', 'NFR-06'],
      suggested_roles: ['admin'],
      rail_gate: 'admin_configure',
    },
    /* CLM-111 — the base MSA (FR-37, DR-11). Read by Admin and Legal
       (clause:review_match, D-2.1-11); nominated by Admin (admin:configure). */
    {
      id: 'base-msa',
      path: '/admin/base-msa',
      label: 'Base MSA',
      summary:
        'The signed contract nominated as cloudEQ’s base MSA, and every earlier version — who set each one and when. Admins nominate or replace it; Legal reads it. Being nominated changes nothing about the contract itself.',
      requirements: ['FR-37', 'DR-11', 'NFR-06', 'NFR-07'],
      suggested_roles: ['admin', 'legal_reviewer'],
      rail_gate: 'admin_or_legal',
    },
    /* CLM-109 — the admin-defined extracted-field set (FR-33, DR-09, NFR-17,
       CR-07). Admin only (admin:configure, R-109-14), so CLM-107's narrower rail gate. */
    {
      id: 'extraction-fields',
      path: '/admin/extraction-fields',
      label: 'Extracted fields',
      summary:
        'The fields extraction asks for beyond the governed ones: add, edit, retire or bring one back without a release. Each is a list entry the request already carries, checked before it is saved, and its values arrive in the verification queue.',
      requirements: ['FR-33', 'DR-09', 'NFR-17', 'CR-07'],
      suggested_roles: ['admin'],
      rail_gate: 'admin_configure',
    },
    /* CLM-110 — the contract-type decision list (FR-35, FR-34, FR-36, DR-10, CR-08).
       Admin only (admin:configure, D-110-2: only an Admin confirms or corrects a
       type), so the narrower rail gate. */
    {
      id: 'type-proposals',
      path: '/admin/type-proposals',
      label: 'Type proposals',
      summary:
        'Where an administrator confirms or corrects each contract’s type: the AI’s reading beside the working type or Type pending, by cause, with the model behind it. A type the file name and the AI agree on is confirmed with no administrator step. When auto-approval for AI-decided types is on, most AI-decided MSAs and NDAs are approved by policy, and one in five still comes here. One decision sets the agreement and its amendments, and never changes a contract ID.',
      requirements: ['FR-35', 'FR-34', 'FR-36', 'DR-10', 'CR-08'],
      suggested_roles: ['admin'],
      rail_gate: 'admin_configure',
    },
    /* CLM-108 — the per-field verification policy (FR-31, FR-32, NFR-16, CR-06). Admin sets it
       (admin:configure); Legal co-signs it (compliance:cosign_verification_settings, D-2.1-7), so
       the entry is CLM-111's admin-or-Legal gate. Extraction accuracy's neighbour in purpose and
       the page it links to (R-108-20); appended here in order (MERGE-ORDER: append at END). */
    {
      id: 'verification-policy',
      path: '/admin/verification-policy',
      label: 'Auto-approval',
      summary:
        'Per field, the organisation-wide accuracy and whether its values skip human review. An admin proposes the threshold and sets each field’s policy; a different Legal holder co-signs before anything that skips review takes effect. Auto-approved values are settled by policy, never shown as verified by a person.',
      requirements: ['FR-31', 'FR-32', 'NFR-16', 'CR-06'],
      suggested_roles: ['admin', 'legal_reviewer'],
      rail_gate: 'admin_or_legal',
    },
    /* CLM-113 + CLM-114 — the vendor-list import (FR-39, FR-40, FR-40a, DR-12, NFR-19). Admin only
       (party:import_vendors, R-113-20), so CLM-107's narrower rail gate, which mirrors the Admin role and
       decides nothing (the API is the boundary). Appended at END (MERGE-ORDER). */
    {
      id: 'vendor-import',
      path: '/admin/vendor-import',
      label: 'Vendor list',
      summary:
        'Upload the vendor list from Business Central or a CSV, review what each row would do, then import the rows you approve; nothing is written before that, and nothing is ever deactivated.',
      requirements: ['FR-39', 'FR-40', 'FR-40a', 'DR-12', 'NFR-19'],
      suggested_roles: ['admin'],
      rail_gate: 'admin_configure',
    },
  ],
};

/**
 * CLM-96 (R-96-1) — the Overview section, rendered FIRST, above Contracts.
 * Its own export, like ADMIN_NAV_GROUP, so `NAV_GROUPS` keeps meaning "the ten
 * mock surfaces". No `suggested_roles`: both boards gate on `contract:read`,
 * which every CLM role holds, so nothing is dimmed and nothing is hidden — a
 * practice reader sees their own scope's figures, never a 403.
 */
export const ANALYTICS_NAV_GROUP: NavGroup = {
  id: 'analytics',
  label: 'Overview',
  surfaces: [
    {
      id: 'analytics-dashboard',
      path: '/analytics/dashboard',
      label: 'Dashboard',
      summary:
        "Lands here from '/'. Needs-attention strip and the due-for-renewal list on top; then active and expiring contracts, overdue obligations, unacknowledged alerts, fields awaiting verification, time to verify, status mix, and the per-client and per-reviewer tables.",
      requirements: ['FR-07', 'O-1', 'NFR-06', 'CR-01'],
    },
    {
      id: 'analytics-overview',
      path: '/analytics/overview',
      label: 'Portfolio',
      summary:
        'Every contract in your scope by pipeline state and by type — counts, today’s intake and a period filter. No money figures: contract value is an extracted phrase, not a number.',
      requirements: ['FR-07', 'NFR-06', 'O-1'],
    },
  ],
};

/** Every group the shell knows, for the consumers that want all of them. */
const ALL_NAV_GROUPS: readonly NavGroup[] = [...NAV_GROUPS, ANALYTICS_NAV_GROUP, ADMIN_NAV_GROUP];

/**
 * The contract record takes a contract key. `:contract_key` is snake_case to
 * match the identifier convention (CLAUDE.md rule 12) — the value comes
 * straight from the API and goes straight back to it.
 *
 * The nav rail points at the parameterless path, which renders a "pick a
 * contract" stub; a keyed record is reached from search or from the attention
 * list.
 */
export const CONTRACT_RECORD_PATH = '/contracts/record/:contract_key';

/**
 * One clause of the library, by its natural key (CLM-55). Under `/clauses`, so
 * `locateSurface`'s longest-prefix match breadcrumbs it to Clause library.
 */
export const CLAUSE_DETAIL_PATH = '/clauses/:clause_code';

/**
 * One base-MSA deviation report (CLM-112), under its subject's record, so
 * `locateSurface`'s longest-prefix match breadcrumbs it to Contract record. Not
 * in the nav: a report is reached from the record's "Compare with base MSA"
 * section, which knows which contract it is about.
 */
export const BASE_DEVIATION_REPORT_PATH = '/contracts/record/:contract_key/base-deviation/:report_id';

/** Where '/' lands. CLM-90 (D-90-1): the Dashboard, for every role. */
export const DEFAULT_PATH = '/analytics/dashboard';

export const ALL_SURFACES: readonly SurfaceDefinition[] = ALL_NAV_GROUPS.flatMap(
  (group) => group.surfaces,
);

/**
 * Look up a surface by id. Throws rather than returning undefined: an id that
 * is not in the table is a typo in a route module, and it should break the
 * build-and-run loop immediately rather than render an empty header.
 */
export function requireSurface(id: string): SurfaceDefinition {
  const found = ALL_SURFACES.find((surface) => surface.id === id);
  if (!found) {
    throw new Error(`Unknown CLM surface id "${id}" — it is not in the surface table.`);
  }
  return found;
}

/** Group + surface for a pathname, for the breadcrumb. Longest match wins. */
export function locateSurface(
  pathname: string,
): { group: NavGroup; surface: SurfaceDefinition } | null {
  let best: { group: NavGroup; surface: SurfaceDefinition } | null = null;

  for (const group of ALL_NAV_GROUPS) {
    for (const surface of group.surfaces) {
      const isMatch = pathname === surface.path || pathname.startsWith(`${surface.path}/`);
      if (!isMatch) continue;
      if (!best || surface.path.length > best.surface.path.length) {
        best = { group, surface };
      }
    }
  }

  return best;
}
